import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AthleteView,
  DefinitionView,
  DepartmentView,
  PageResponse,
  RosterEntryView,
  SessionSummaryView,
  SessionView,
  SupportedLocale,
  TeamView,
  TrainingGroupView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  canSubmitSession,
  emptySessionForm,
  localizedText,
  sessionRequestBody,
  toggled,
  withAdditions,
  type SessionForm,
} from './assessments';
import { athleteDisplayName, athleteListPath, formatCalendarDate } from './athletes';
import { SessionEntry } from './SessionEntry';

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

const todayInUtc = (): string => new Date().toISOString().slice(0, 10);

export interface SessionsSectionProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  departments: readonly DepartmentView[];
  definitions: readonly DefinitionView[] | null;
  initialSessionId?: string | undefined;
}

interface AthletePickerProps {
  locale: SupportedLocale;
  base: string;
  selected: readonly string[];
  onToggle: (athleteId: string) => void;
}

interface RosterPickerProps {
  locale: SupportedLocale;
  base: string;
  departmentId: string;
  onAdd: (athleteIds: string[]) => void;
}

interface RosterChoice {
  key: string;
  label: string;
  membersPath: string;
}

/** Adds the current members of a team or training group to the selection in one step. */
function RosterPicker({ locale, base, departmentId, onAdd }: RosterPickerProps): React.JSX.Element {
  const [choices, setChoices] = useState<RosterChoice[]>([]);
  const [chosen, setChosen] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [teams, groups] = await Promise.all([
          apiRequest<TeamView[]>(`${base}/teams`),
          apiRequest<TrainingGroupView[]>(`${base}/training-groups`),
        ]);
        if (cancelled) return;
        setChoices([
          ...teams
            .filter((team) => team.departmentId === departmentId && team.status !== 'archived')
            .map((team) => ({
              key: `team:${team.id}`,
              label: `${translate(locale, 'assessments.session.team')}: ${team.name}`,
              membersPath: `${base}/teams/${team.id}/members`,
            })),
          ...groups
            .filter((group) => group.departmentId === departmentId && group.status === 'active')
            .map((group) => ({
              key: `group:${group.id}`,
              label: `${translate(locale, 'assessments.session.group')}: ${group.name}`,
              membersPath: `${base}/training-groups/${group.id}/members`,
            })),
        ]);
      } catch {
        if (!cancelled) setChoices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base, departmentId, locale]);

  async function addRoster(): Promise<void> {
    const choice = choices.find((candidate) => candidate.key === chosen);
    if (choice === undefined) return;
    try {
      const entries = await apiRequest<RosterEntryView[]>(choice.membersPath);
      setFailed(false);
      onAdd(entries.map((entry) => entry.athleteId));
    } catch {
      setFailed(true);
    }
  }

  if (choices.length === 0) return <></>;
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
      <TextField
        select
        size="small"
        sx={{ minWidth: { xs: '100%', sm: 240 } }}
        label={translate(locale, 'assessments.session.rosterChoice')}
        value={chosen}
        onChange={(event) => {
          setChosen(event.target.value);
        }}
      >
        {choices.map((choice) => (
          <MenuItem key={choice.key} value={choice.key}>
            {choice.label}
          </MenuItem>
        ))}
      </TextField>
      <Button
        sx={{ minHeight: 44 }}
        disabled={chosen === ''}
        onClick={() => {
          void addRoster();
        }}
      >
        {translate(locale, 'assessments.session.addRoster')}
      </Button>
      {failed && <Alert severity="error">{translate(locale, 'error.unexpected')}</Alert>}
    </Box>
  );
}

/** Finds athletes on the server (never the whole club in the browser) and keeps the chosen ones. */
function AthletePicker({
  locale,
  base,
  selected,
  onToggle,
}: AthletePickerProps): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [athletes, setAthletes] = useState<AthleteView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(
    async (reset: boolean, after: string | null) => {
      try {
        const page = await apiRequest<PageResponse<AthleteView>>(
          athleteListPath(base, { search, cursor: reset ? null : after }),
        );
        setAthletes((current) => (reset ? page.items : [...current, ...page.items]));
        setCursor(page.nextCursor);
      } catch {
        setAthletes([]);
        setCursor(null);
      } finally {
        setLoaded(true);
      }
    },
    [base, search],
  );

  useEffect(() => {
    // Typing waits a moment so every keystroke does not become a request.
    const timer = setTimeout(() => {
      void load(true, null);
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [load]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 240 } }}
          label={translate(locale, 'assessments.session.search')}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <Chip
          label={`${String(selected.length)} ${translate(locale, 'assessments.session.selected')}`}
          color={selected.length > 0 ? 'primary' : 'default'}
        />
      </Box>
      {loaded && athletes.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {translate(locale, 'assessments.session.noAthletes')}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
        {athletes.map((athlete) => (
          <FormControlLabel
            key={athlete.id}
            control={
              <Checkbox
                checked={selected.includes(athlete.id)}
                onChange={() => {
                  onToggle(athlete.id);
                }}
              />
            }
            label={athleteDisplayName(athlete, locale)}
          />
        ))}
      </Box>
      {cursor !== null && (
        <Button
          size="small"
          onClick={() => {
            void load(false, cursor);
          }}
        >
          {translate(locale, 'assessments.session.loadMore')}
        </Button>
      )}
    </Box>
  );
}

/** Assessment sessions: the list, a form to start one, and the entry sheet of the opened one. */
export function SessionsSection({
  locale,
  base,
  csrfToken,
  departments,
  definitions,
  initialSessionId,
}: SessionsSectionProps): React.JSX.Element {
  const [sessions, setSessions] = useState<SessionSummaryView[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(initialSessionId ?? null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<SessionForm>(() =>
    emptySessionForm(departments[0]?.id ?? '', todayInUtc()),
  );
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  const load = useCallback(async () => {
    try {
      setSessions(await apiRequest<SessionSummaryView[]>(`${base}/assessment-sessions`));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const departmentChoice =
    form.departmentId === '' ? (departments[0]?.id ?? '') : form.departmentId;
  const usableDefinitions = (definitions ?? []).filter(
    (definition) =>
      definition.departmentId === null || definition.departmentId === departmentChoice,
  );

  async function create(): Promise<void> {
    setErrorKey(null);
    try {
      const created = await apiRequest<SessionView>(`${base}/assessment-sessions`, {
        method: 'POST',
        body: sessionRequestBody({ ...form, departmentId: departmentChoice }),
        csrfToken,
      });
      setCreating(false);
      setForm(emptySessionForm(departments[0]?.id ?? '', todayInUtc()));
      await load();
      setOpenId(created.id);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box
      component="section"
      sx={{ mt: 4 }}
      aria-label={translate(locale, 'assessments.sessions.title')}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'assessments.sessions.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {sessions?.length === 0 && (
        <Typography color="text.secondary">
          {translate(locale, 'assessments.sessions.empty')}
        </Typography>
      )}
      <Stack spacing={1}>
        {sessions?.map((session) => (
          <Paper key={session.id} variant="outlined" sx={{ padding: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 600 }}>
                {formatCalendarDate(session.sessionDate, locale)}
              </Typography>
              {session.context !== null && (
                <Typography variant="body2" color="text.secondary">
                  {session.context}
                </Typography>
              )}
              <Chip
                size="small"
                label={`${String(session.participantCount)} ${translate(locale, 'assessments.session.participants')}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${String(session.draftCount)} ${translate(locale, 'assessments.session.drafts')}`}
              />
              <Chip
                size="small"
                variant="outlined"
                color="success"
                label={`${String(session.publishedCount)} ${translate(locale, 'assessments.session.published')}`}
              />
              <Button
                size="small"
                variant="outlined"
                sx={{ minHeight: 40, ml: 'auto' }}
                onClick={() => {
                  setOpenId(openId === session.id ? null : session.id);
                }}
              >
                {translate(
                  locale,
                  openId === session.id ? 'assessments.session.close' : 'assessments.session.open',
                )}
              </Button>
            </Box>
            {openId === session.id && (
              <SessionEntry
                locale={locale}
                base={base}
                csrfToken={csrfToken}
                sessionId={session.id}
                onClose={() => {
                  setOpenId(null);
                }}
                onChanged={load}
              />
            )}
          </Paper>
        ))}
      </Stack>
      {!creating && (
        <Button
          variant="contained"
          sx={{ mt: 2, minHeight: 44 }}
          onClick={() => {
            setCreating(true);
          }}
        >
          {translate(locale, 'assessments.session.new')}
        </Button>
      )}
      {creating && (
        <Box
          component="form"
          sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <TextField
            select
            size="small"
            sx={{ minWidth: 180 }}
            label={translate(locale, 'assessments.protocol.department')}
            value={departmentChoice}
            onChange={(event) => {
              setForm({ ...form, departmentId: event.target.value, definitionVersionIds: [] });
            }}
          >
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            required
            type="date"
            label={translate(locale, 'assessments.session.date')}
            value={form.sessionDate}
            onChange={(event) => {
              setForm({ ...form, sessionDate: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 260 }, flexGrow: 1 }}
            label={translate(locale, 'assessments.session.context')}
            value={form.context}
            onChange={(event) => {
              setForm({ ...form, context: event.target.value });
            }}
          />
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" component="h3">
              {translate(locale, 'assessments.session.protocols')}
            </Typography>
            {usableDefinitions.length === 0 && (
              <Typography color="text.secondary">
                {translate(locale, 'assessments.session.noProtocols')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
              {usableDefinitions.map((definition) => (
                <FormControlLabel
                  key={definition.versionId}
                  control={
                    <Checkbox
                      checked={form.definitionVersionIds.includes(definition.versionId)}
                      onChange={() => {
                        setForm({
                          ...form,
                          definitionVersionIds: toggled(
                            form.definitionVersionIds,
                            definition.versionId,
                          ),
                        });
                      }}
                    />
                  }
                  label={`${localizedText(definition.titles, locale)} (${translate(locale, 'assessments.protocol.version')} ${String(definition.versionNumber)})`}
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" component="h3">
              {translate(locale, 'assessments.session.athletes')}
            </Typography>
            <RosterPicker
              locale={locale}
              base={base}
              departmentId={departmentChoice}
              onAdd={(athleteIds) => {
                setForm({ ...form, athleteIds: withAdditions(form.athleteIds, athleteIds) });
              }}
            />
            <AthletePicker
              locale={locale}
              base={base}
              selected={form.athleteIds}
              onToggle={(athleteId) => {
                setForm({ ...form, athleteIds: toggled(form.athleteIds, athleteId) });
              }}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            sx={{ minHeight: 44 }}
            disabled={!canSubmitSession({ ...form, departmentId: departmentChoice })}
          >
            {translate(locale, 'assessments.session.create')}
          </Button>
          <Button
            sx={{ minHeight: 44 }}
            onClick={() => {
              setCreating(false);
            }}
          >
            {translate(locale, 'assessments.protocols.cancel')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
